'use client'

import { Fragment } from 'react'
import Image from 'next/image'
import { Button } from '@acko/button'
import { Typography } from '@acko/typography'
import { ABHA_HERO_WIDGET_SECONDARY_ARROW_ICON } from '@components/NewProduct/constants'
import type { NewProductStickyFooterData } from 'src/mocks/newProductPage.mock'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'

type AbhaStickyFooterProps = {
  stickyFooterData?: NewProductStickyFooterData
  primaryCtaText: string
  alreadyHaveAbhaText: string
  onCreateClick: () => void
  onExistingAbhaClick: () => void
  isVisible: boolean
}

export const AbhaStickyFooter = ({
  stickyFooterData,
  primaryCtaText,
  alreadyHaveAbhaText,
  onCreateClick,
  onExistingAbhaClick,
  isVisible,
}: AbhaStickyFooterProps) => {
  const productInfoTitle = stickyFooterData?.productInfo?.title ?? stickyFooterData?.title
  const productInfoSubtitle = stickyFooterData?.productInfo?.subtitle ?? stickyFooterData?.subtitle
  const footerBenefits = stickyFooterData?.benefits?.filter(Boolean) ?? []
  const hasBenefits = footerBenefits.length > 0

  return (
    <aside
      aria-label="ABHA quick actions"
      aria-hidden={!isVisible}
      className={`fixed inset-x-0 bottom-0 z-[50] border-t border-[#e7e7e7] bg-white shadow-[0_-2px_12px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : 'pointer-events-none translate-y-full'
      }`}>
      <div className="mx-auto box-border flex w-full max-w-[1280px] flex-col items-center gap-[20px] px-[16px] py-[16px] md:px-[32px] lg:flex-row lg:justify-between lg:gap-10 lg:px-[80px] lg:py-[12px] min-[1440px]:box-content">
        <PropertyControlledComponent
          controllerProperty={Boolean(productInfoTitle || productInfoSubtitle)}
          fallback={null}>
          <div className="hidden w-fit shrink-0 lg:block">
            <PropertyControlledComponent controllerProperty={productInfoTitle} fallback={null}>
              <Typography as="p" scale="base" emphasis="bold" color="primary" className="text-lg leading-6">
                {productInfoTitle}
              </Typography>
            </PropertyControlledComponent>
            <PropertyControlledComponent controllerProperty={productInfoSubtitle} fallback={null}>
              <Typography as="p" scale="sm" color="secondary" className="mt-0.5 text-base leading-[1.375rem]">
                {productInfoSubtitle}
              </Typography>
            </PropertyControlledComponent>
          </div>
        </PropertyControlledComponent>

        <PropertyControlledComponent controllerProperty={hasBenefits} fallback={null}>
          <div className="hidden w-fit items-center gap-[16px] lg:flex">
            {footerBenefits.map((benefit, index) => (
              <Fragment key={`${benefit}-${index}`}>
                <Typography
                  as="p"
                  scale="xs"
                  emphasis="medium"
                  color="secondary"
                  align="center"
                  className="w-fit whitespace-pre-line">
                  {benefit}
                </Typography>
                <PropertyControlledComponent controllerProperty={index < footerBenefits.length - 1} fallback={null}>
                  <div aria-hidden className="w-px self-stretch shrink-0 bg-[var(--solidGrey500)]" />
                </PropertyControlledComponent>
              </Fragment>
            ))}
          </div>
        </PropertyControlledComponent>

        <div className="flex w-full flex-col items-center gap-[8px] lg:w-auto lg:flex-row lg:gap-[16px]">
          <Button
            type="button"
            variant="ghost"
            size="md"
            className="order-2 w-full lg:order-none [&_.acko-button-label]:gap-[8px]"
            disabled={!isVisible}
            tabIndex={isVisible ? undefined : -1}
            onClick={onExistingAbhaClick}>
            <span>{alreadyHaveAbhaText}</span>
            <Image
              src={ABHA_HERO_WIDGET_SECONDARY_ARROW_ICON}
              alt="Purple arrow"
              width={16}
              height={16}
              aria-hidden
              className="block h-[16px] w-[16px] shrink-0"
            />
          </Button>

          <Button
            type="button"
            variant="primary"
            size="lg"
            className="order-1 w-full lg:order-none lg:w-auto"
            disabled={!isVisible}
            tabIndex={isVisible ? undefined : -1}
            onClick={onCreateClick}>
            <span>{primaryCtaText}</span>
          </Button>
        </div>
      </div>
    </aside>
  )
}
